// src/components/FormField.jsx
// ─────────────────────────────────────────────────────────────
// Univerzální komponenta pro jedno pole formuláře
// ─────────────────────────────────────────────────────────────
import React, { useState, useRef } from 'react';
import { MAX_FILE_SIZE_MB, ACCEPTED_FILE_TYPES } from '../lib/questions';

export default function FormField({ q, value, onChange, error, onFileChange }) {
  const [charCount, setCharCount] = useState(value ? value.length : 0);
  const [fileName, setFileName]   = useState('');
  const [dragging, setDragging]   = useState(false);
  const fileRef = useRef(null);

  const handleChange = (e) => {
    const val = e.target.value;
    if (q.maxLen && val.length > q.maxLen) return; // hard cap
    setCharCount(val.length);
    onChange(q.key, val);
  };

  const handleFile = (file) => {
    if (!file) return;
    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      alert(`Soubor je příliš velký. Maximum je ${MAX_FILE_SIZE_MB} MB.`);
      return;
    }
    setFileName(file.name);
    onFileChange && onFileChange(q.key, file);
  };

  const clearFile = () => {
    setFileName('');
    if (fileRef.current) fileRef.current.value = '';
    onFileChange && onFileChange(q.key, null);
  };

  const pct = q.maxLen ? (charCount / q.maxLen) * 100 : 0;
  const overWarn = pct >= 90;

  const labelStyle = {
    fontSize: 14,
    fontWeight: 500,
    color: '#1A1A1A',
    cursor: 'pointer',
  };
  const hintStyle = {
    fontSize: 13,
    color: '#555',
    marginBottom: 10,
    lineHeight: 1.6,
    paddingLeft: 32,
  };
  const errStyle = {
    fontSize: 12,
    color: '#C0392B',
    marginTop: 5,
    paddingLeft: 32,
    display: error ? 'block' : 'none',
  };
  const charStyle = {
    fontSize: 11,
    color: overWarn ? '#C0392B' : '#bbb',
    textAlign: 'right',
    marginTop: 3,
    fontWeight: overWarn ? 600 : 400,
  };

  return (
    <div style={{ marginBottom: 24 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 6 }}>
        <span style={{
          fontSize: 11, fontWeight: 700, color: '#5b14b3',
          background: 'rgba(91,20,179,0.08)', borderRadius: 4,
          padding: '2px 7px', flexShrink: 0,
        }}>
          {q.qNum}
        </span>
        <label htmlFor={`f_${q.key}`} style={labelStyle}>
          {q.label}
          {q.required && (
            <span
              title="Povinné pole"
              style={{
                color: '#5b14b3', marginLeft: 4, cursor: 'help',
                position: 'relative', display: 'inline-block',
              }}
            >*</span>
          )}
        </label>
      </div>

      {/* Hint */}
      {q.hint && <p style={hintStyle}>{q.hint}</p>}

      {/* Input */}
      {q.type === 'textarea' && (
        <textarea
          id={`f_${q.key}`}
          rows={q.rows || 4}
          value={value || ''}
          onChange={handleChange}
          placeholder={q.placeholder}
          maxLength={q.maxLen}
          className={error ? 'error' : ''}
          style={{
            width: '100%', background: '#FAFAFA',
            border: `1.5px solid ${error ? '#C0392B' : '#E8E4DC'}`,
            borderRadius: 8, color: '#1A1A1A', fontFamily: 'GT America, sans-serif',
            fontSize: 14, padding: '11px 14px', outline: 'none', resize: 'vertical',
            transition: 'border-color 0.18s, box-shadow 0.18s',
            boxShadow: error ? '0 0 0 3px rgba(192,57,43,0.1)' : 'none',
          }}
          onFocus={e => { e.target.style.borderColor = '#5b14b3'; e.target.style.boxShadow = '0 0 0 3px rgba(91,20,179,0.1)'; e.target.style.background = '#fff'; }}
          onBlur={e  => { e.target.style.borderColor = error ? '#C0392B' : '#E8E4DC'; e.target.style.boxShadow = error ? '0 0 0 3px rgba(192,57,43,0.1)' : 'none'; e.target.style.background = '#FAFAFA'; }}
        />
      )}

      {(q.type === 'text' || q.type === 'email' || q.type === 'tel' || q.type === 'url') && (
        <input
          id={`f_${q.key}`}
          type={q.type}
          value={value || ''}
          onChange={handleChange}
          placeholder={q.placeholder}
          maxLength={q.maxLen}
          style={{
            width: '100%', background: '#FAFAFA',
            border: `1.5px solid ${error ? '#C0392B' : '#E8E4DC'}`,
            borderRadius: 8, color: '#1A1A1A', fontFamily: 'GT America, sans-serif',
            fontSize: 14, padding: '11px 14px', outline: 'none',
            transition: 'border-color 0.18s, box-shadow 0.18s',
            boxShadow: error ? '0 0 0 3px rgba(192,57,43,0.1)' : 'none',
          }}
          onFocus={e => { e.target.style.borderColor = '#5b14b3'; e.target.style.boxShadow = '0 0 0 3px rgba(91,20,179,0.1)'; e.target.style.background = '#fff'; }}
          onBlur={e  => { e.target.style.borderColor = error ? '#C0392B' : '#E8E4DC'; e.target.style.boxShadow = error ? '0 0 0 3px rgba(192,57,43,0.1)' : 'none'; e.target.style.background = '#FAFAFA'; }}
        />
      )}

      {q.type === 'file' && (
        <div
          onDragOver={e => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={e => {
            e.preventDefault(); setDragging(false);
            const f = e.dataTransfer.files[0];
            if (f) handleFile(f);
          }}
          onClick={() => fileRef.current?.click()}
          style={{
            border: `2px dashed ${dragging ? '#5b14b3' : error ? '#C0392B' : '#E8E4DC'}`,
            borderRadius: 8,
            padding: '24px 20px',
            textAlign: 'center',
            cursor: 'pointer',
            background: dragging ? 'rgba(91,20,179,0.03)' : '#FAFAFA',
            transition: 'all 0.18s',
          }}
        >
          <input
            ref={fileRef}
            type="file"
            accept={ACCEPTED_FILE_TYPES}
            style={{ display: 'none' }}
            onChange={e => handleFile(e.target.files[0])}
          />
          {fileName ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                background: 'rgba(46,125,81,0.08)', border: '1px solid rgba(46,125,81,0.25)',
                borderRadius: 6, padding: '6px 12px', fontSize: 13, color: '#2E7D51',
              }}>
                📎 {fileName}
              </span>
              <button
                type="button"
                onClick={e => { e.stopPropagation(); clearFile(); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#888', fontSize: 16 }}
              >
                ✕
              </button>
            </div>
          ) : (
            <>
              <div style={{ fontSize: 28, marginBottom: 6 }}>📄</div>
              <p style={{ fontSize: 13, color: '#888', lineHeight: 1.6 }}>
                <strong style={{ color: '#5b14b3' }}>Klikni nebo přetáhni soubor sem</strong><br />
                Max. {MAX_FILE_SIZE_MB} MB · PDF, Word, obrázek, video, zvuk
              </p>
            </>
          )}
        </div>
      )}

      {/* Char counter */}
      {q.maxLen && q.type !== 'file' && (
        <div style={charStyle}>{charCount} / {q.maxLen}</div>
      )}

      {/* Error */}
      <div style={errStyle}>{error}</div>
    </div>
  );
}
